import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchBillingEntityData,
  fetchCropYearData,
  fetchFarmData,
  fetchUnMappedFieldsData,
  postNewBillingEntityData,
  postNewFarmData,
  postNewFarmNameData,
  renameBillingEntityData,
  renameLandlordData,
  saveNewLandlordFieldMapping,
} from "../services/addfarm-billingentity.api";
import { Option } from "../screens/ServiceAgreements/types";

export interface BillingEntityDropdownItem {
  id: number;
  name: string;
}
export interface FarmDropdownItem {
  id: number;
  name: string;
}
export interface CropYearDropdownItem {
  id: number;
  name: string;
}
interface FarmDetails {
  loading: boolean;
  error: string | null;
  billingEntities: BillingEntityDropdownItem[];
  farmNames: FarmDropdownItem[];
  cropYear: CropYearDropdownItem[];
  fields: Option[];
}
const initialState: FarmDetails = {
  loading: false,
  error: null,
  billingEntities: [],
  farmNames: [],
  cropYear: [],
  fields: [],
};

export const postNewFarmDetails = createAsyncThunk(
  "newfarmdata/post",
  async (value: any) => {
    const response = await postNewFarmData(value);
    return response.data;
  }
);

export const addNewLandlordFarmMapping = createAsyncThunk(
  "addnewlandlordmapping/post",
  async (value: any) => {
    const response = await saveNewLandlordFieldMapping(value);
    return response.data;
  }
);

export const fetchBillingEntityDetails = createAsyncThunk(
  "billingentity/fetch",
  async () => {
    const response = await fetchBillingEntityData();
    return response.data;
  }
);

export const fetchUnMappedFields = createAsyncThunk(
  "unmappedFields/fetch",
  async () => {
    const response = await fetchUnMappedFieldsData();
    return response.data;
  }
);

export const fetchFarmDetails = createAsyncThunk(
  "farmnames/fetch",
  async () => {
    const response = await fetchFarmData();
    return response.data;
  }
);
export const fetchCropYearDetails = createAsyncThunk(
  "cropyear/fetch",
  async () => {
    const response = await fetchCropYearData();
    return response.data;
  }
);
export const postNewBillingEntityDetails = createAsyncThunk(
  "newbillingentity/post",
  async (data: any) => {
    const response = await postNewBillingEntityData(data);
    return response?.data;
  }
);
export const postNewFarmNameDetails = createAsyncThunk(
  "newfarmname/post",
  async (data: any) => {
    const response = await postNewFarmNameData(data);
    return response?.data;
  }
);

export const renameLandlord = createAsyncThunk(
  "landlord/rename",
  async (data: { id: number; landlordName: string }) => {
    const response = await renameLandlordData(data);
    return response?.data;
  }
);

export const renameBillingEntity = createAsyncThunk(
  "billingentity/rename",
  async (data: { id: number; billingName: string }) => {
    const response = await renameBillingEntityData(data);
    return response?.data;
  }
);

const addFarmSlice = createSlice({
  name: "addfarm",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(postNewFarmDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postNewFarmDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(postNewFarmDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addNewLandlordFarmMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewLandlordFarmMapping.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addNewLandlordFarmMapping.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchBillingEntityDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.billingEntities = action.payload;
      })
      .addCase(fetchUnMappedFields.fulfilled, (state, action) => {
        state.loading = false;
        state.fields = action.payload;
      })
      .addCase(fetchFarmDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.farmNames = action.payload;
      })
      .addCase(postNewFarmNameDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(postNewFarmNameDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(postNewFarmNameDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCropYearDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.cropYear = action.payload;
      })
      .addCase(postNewBillingEntityDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(postNewBillingEntityDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(postNewBillingEntityDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(renameLandlord.pending, (state) => {
        state.loading = true;
      })
      .addCase(renameLandlord.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(renameLandlord.rejected, (state) => {
        state.loading = false;
      })
      .addCase(renameBillingEntity.pending, (state) => {
        state.loading = true;
      })
      .addCase(renameBillingEntity.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(renameBillingEntity.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default addFarmSlice.reducer;